// src/pages/Admin/Adherents/AdherentsCharts.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from "recharts";

/* Tooltip simple (dark-aware) */
const CustomTooltip = ({ active, payload, label, isDark, labelUnit }) => {
  if (!active || !payload?.length) return null;
  const val = payload[0].value;
  return (
    <div
      className={`rounded-xl border px-3 py-2 shadow-sm ${
        isDark
          ? "bg-slate-900 border-slate-700 text-slate-100"
          : "bg-white border-slate-200 text-slate-800"
      }`}
    >
      <div className="text-[11px] opacity-70">{label}</div>
      <div className="text-sm font-semibold">
        {val.toLocaleString("fr-FR")} {labelUnit || ""}
      </div>
    </div>
  );
};

export default function AdherentsCharts({
  rows = [],
  groups = [],
  activeOnly = true,
}) {
  const [isDark, setIsDark] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false
  );

  useEffect(() => {
    if (typeof MutationObserver === "undefined") return;
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains("dark"))
    );
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => obs.disconnect();
  }, []);

  // Données agrégées : statuts
  const byStatus = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const k = r.statut_inscription || "—";
      map.set(k, (map.get(k) || 0) + 1);
    });
    return Array.from(map, ([statut, count]) => ({ statut, count })).sort(
      (a, b) => b.count - a.count
    );
  }, [rows]);

  // Données agrégées : groupes (barres verticales)
  // Si group.is_active est défini (bool), on ne garde que ceux à true.
  const byGroup = useMemo(() => {
    const isActive = (g) =>
      g && (typeof g.is_active === "boolean" ? g.is_active : true);

    const map = new Map();
    rows.forEach((r) =>
      (r.Groups || []).forEach((g) => {
        if (!isActive(g)) return;
        const k = g?.nom || "—";
        map.set(k, (map.get(k) || 0) + 1);
      })
    );

    const arr = Array.from(map, ([groupe, count]) => ({ groupe, count })).sort(
      (a, b) => b.count - a.count
    );

    // Option lisibilité: limiter à 24 groupes max (sinon labels illisibles)
    if (arr.length <= 24) return arr;
    return arr.slice(0, 24);
  }, [rows]);

  const grid = isDark ? "#334155" : "#e2e8f0";
  const tick = isDark ? "#cbd5e1" : "#334155";
  const cursorFill = isDark
    ? "rgba(148,163,184,0.12)"
    : "rgba(148,163,184,0.15)";
  const topColor = isDark ? "#9CA3FF" : "#A5B4FC";
  const bottomColor = isDark ? "#6C72D9" : "#818CF8";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
      {/* Statuts (bar chart) */}
      <div className="w-full h-[320px] rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-3">
        <div className="text-sm font-semibold mb-2">Répartition par statut</div>
        <ResponsiveContainer width="100%" height="90%">
          <BarChart
            data={byStatus}
            margin={{ top: 10, right: 16, left: 0, bottom: 40 }}
          >
            <defs>
              <linearGradient id="barFillStatus" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={topColor} stopOpacity={0.95} />
                <stop
                  offset="100%"
                  stopColor={bottomColor}
                  stopOpacity={0.85}
                />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={grid} />
            <XAxis
              dataKey="statut"
              height={40}
              interval={0}
              angle={-25}
              dy={20}
              tick={{ fill: tick, fontSize: 12 }}
            />
            <YAxis tick={{ fill: tick, fontSize: 12 }} width={36} />
            <Tooltip
              cursor={{ fill: cursorFill }}
              content={<CustomTooltip isDark={isDark} labelUnit="adh." />}
            />
            <Bar
              dataKey="count"
              fill="url(#barFillStatus)"
              radius={[8, 8, 0, 0]}
            >
              <LabelList
                dataKey="count"
                position="top"
                formatter={(v) => v.toLocaleString("fr-FR")}
                style={{ fill: isDark ? "#e2e8f0" : "#0f172a", fontSize: 12 }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Groupes (bar chart vertical) */}
      <div className="w-full h-[320px] rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-3">
        <div className="text-sm font-semibold mb-2">
          Répartition par groupe (actifs)
        </div>
        <ResponsiveContainer width="100%" height="90%">
          <BarChart
            data={byGroup}
            margin={{ top: 10, right: 16, left: 0, bottom: 80 }}
          >
            <defs>
              <linearGradient id="barFillGroups" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={topColor} stopOpacity={0.95} />
                <stop
                  offset="100%"
                  stopColor={bottomColor}
                  stopOpacity={0.85}
                />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={grid} />
            <XAxis
              dataKey="groupe"
              height={60}
              interval={0}
              angle={-30}
              dy={40}
              tick={{ fill: tick, fontSize: 11 }}
            />
            <YAxis tick={{ fill: tick, fontSize: 12 }} width={36} />
            <Tooltip
              cursor={{ fill: cursorFill }}
              content={<CustomTooltip isDark={isDark} labelUnit="adh." />}
            />
            <Bar
              dataKey="count"
              fill="url(#barFillGroups)"
              radius={[8, 8, 0, 0]}
            >
              <LabelList
                dataKey="count"
                position="top"
                formatter={(v) => v.toLocaleString("fr-FR")}
                style={{ fill: isDark ? "#e2e8f0" : "#0f172a", fontSize: 11 }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
